/**
 * The context menu — `KLQ-20260825-006` §C.1.
 *
 * **One instance for the whole application.** Not one menu per row: a register with 250 rows
 * would otherwise mount 250 popups in order to show one, and every one of them would carry a
 * portal, a keyboard handler and three window listeners. So the state — *which items, at
 * which point* — lives in a provider at the shell, and a surface that wants a menu calls
 * `open()` with a list it built for the rows the person actually pointed at.
 *
 * **It is the same popup and the same item model as `Menu`.** That is the whole point of the
 * split in `MenuPopup.tsx`: right-click and `⋯` are two doors, and an action added to a
 * constructor appears in both without being written twice. Retro rule 12's failure — two
 * copies of a list drifting apart — is not avoided by discipline here, it is unavailable.
 *
 * **Focus goes back where it came from** (§I). A menu opened from a table row returns focus to
 * that table, so Escape leaves somebody exactly where they were rather than at the top of the
 * document with a selection they can no longer act on.
 */

import { createContext, useCallback, useContext, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { drawnActions, MenuPopup } from "./MenuPopup";
import type { MenuItem } from "./MenuPopup";

export interface ContextMenuRequest {
  /** The menu's accessible name, already translated — the primitive has no copy (§C.4). */
  readonly label: string;
  readonly items: readonly MenuItem[];
  /** Viewport coordinates. A keyboard request passes the focused row's own corner. */
  readonly x: number;
  readonly y: number;
  /**
   * What takes focus back when the menu closes on Escape. Usually the element the gesture
   * started on — for a grid that is the table itself, which is the thing that holds the
   * selection and the arrow keys.
   */
  readonly returnFocus?: HTMLElement | null | undefined;
}

export interface ContextMenuApi {
  readonly open: (request: ContextMenuRequest) => void;
  readonly close: () => void;
  readonly isOpen: boolean;
}

const ContextMenuContext = createContext<ContextMenuApi | null>(null);

/**
 * Optional, like the selection provider: a screen with no shell above it — the sign-in page,
 * a print view — has no menu and needs none, and a surface asks rather than assumes.
 */
export function useOptionalContextMenu(): ContextMenuApi | null {
  return useContext(ContextMenuContext);
}

export function useContextMenu(): ContextMenuApi {
  const api = useContext(ContextMenuContext);
  if (api === null) {
    throw new Error("useContextMenu must be used within a <ContextMenuProvider>");
  }
  return api;
}

export function ContextMenuProvider({ children }: { readonly children: ReactNode }) {
  const [request, setRequest] = useState<ContextMenuRequest | null>(null);
  /** Held in a ref as well, because the element to focus is needed after the state is gone. */
  const returnTo = useRef<HTMLElement | null>(null);

  const open = useCallback((next: ContextMenuRequest) => {
    // A menu with nothing to offer is not opened at all. Refusing here rather than in every
    // caller is what lets a surface build its list unconditionally and let the `hidden` rules
    // decide — including the case where they hide everything.
    if (drawnActions(next.items).length === 0) return;
    returnTo.current = next.returnFocus ?? null;
    setRequest(next);
  }, []);

  const close = useCallback(() => {
    setRequest(null);
  }, []);

  const api = useMemo<ContextMenuApi>(
    () => ({ open, close, isOpen: request !== null }),
    [open, close, request],
  );

  return (
    <ContextMenuContext.Provider value={api}>
      {children}
      {request === null ? null : (
        <MenuPopup
          label={request.label}
          items={request.items}
          anchor={{ kind: "point", x: request.x, y: request.y }}
          onClose={(reason) => {
            setRequest(null);
            // `dismissed` and `chosen` both end with the person back where they opened the
            // menu — on a grid, that is the row they were standing on, and `KLQ-20260825-006`
            // §I says so out loud. Only `moved-on` leaves focus alone, because there the
            // person is already on their way somewhere else.
            if (reason !== "moved-on") returnTo.current?.focus();
          }}
        />
      )}
    </ContextMenuContext.Provider>
  );
}
